import type { Coding, CoverageEligibilityResponse } from '@medplum/fhirtypes';
import { POLARIS_INSURANCE_ELIGIBILITY_STATUS_TAG_SYSTEM } from './system';

export const TAG_SYSTEM = 'http://polarissleep.com/tags';
export const HSAT_STATUS_TAG_SYSTEM = `${TAG_SYSTEM}/hsat-status`;

// --- Eligibility / self-referral phase tags ---

export const ELIGIBILITY_CONFIRMATION_TAG: Coding = {
  system: TAG_SYSTEM,
  code: 'eligibility-confirmation',
  display: 'Eligibility Confirmation',
};

export const ELIGIBILITY_CHECK_PAGE_1_TAG: Coding = {
  system: TAG_SYSTEM,
  code: 'eligibility-check-page-1',
  display: 'Eligibility Check Page 1',
};

export const ELIGIBILITY_CHECK_PAGE_2_TAG: Coding = {
  system: TAG_SYSTEM,
  code: 'eligibility-check-page-2',
  display: 'Eligibility Check Page 2',
};

export const STOP_BANG_TAG: Coding = {
  system: TAG_SYSTEM,
  code: 'stop-bang',
  display: 'STOP-BANG Screening',
};

export const EPWORTH_SLEEPINESS_SCALE_TAG: Coding = {
  system: TAG_SYSTEM,
  code: 'epworth-sleepiness-scale',
  display: 'Epworth Sleepiness Scale',
};

export const SLEEP_HISTORY_TAG: Coding = {
  system: TAG_SYSTEM,
  code: 'sleep-history',
  display: 'Sleep History',
};

export const ELIGIBILITY_CHECK_FINISHED_TAG: Coding = {
  system: TAG_SYSTEM,
  code: 'eligibility-check-finished',
  display: 'Eligibility Check Finished',
};

export const ELIGIBILITY_APPOINTMENT_PENDING_TAG: Coding = {
  system: TAG_SYSTEM,
  code: 'eligibility-appointment-pending',
  display: 'Eligibility Appointment Pending',
};

export const ELIGIBILITY_APPOINTMENT_BOOKED_TAG: Coding = {
  system: TAG_SYSTEM,
  code: 'eligibility-appointment-booked',
  display: 'Eligibility Appointment Booked',
};

// --- Onboarding phase tags ---

export const CONSENT_PENDING_TAG: Coding = {
  system: TAG_SYSTEM,
  code: 'consent-pending',
  display: 'Consent Pending',
};

export const WAITING_TRIAGE_RESULT_TAG: Coding = {
  system: TAG_SYSTEM,
  code: 'waiting-triage-result',
  display: 'Waiting Triage Result',
};

// --- PCP referral phase tags ---

export const PCP_REFERRAL_PHASE_1_COMPLETE_TAG: Coding = {
  system: TAG_SYSTEM,
  code: 'pcp-referral-phase-1-complete',
  display: 'PCP Referral Phase 1 Complete',
};

export const PCP_REFERRAL_PHASE_2_COMPLETE_TAG: Coding = {
  system: TAG_SYSTEM,
  code: 'pcp-referral-phase-2-complete',
  display: 'PCP Referral Phase 2 Complete',
};

export const PCP_REFERRAL_PHASE_3_COMPLETE_TAG: Coding = {
  system: TAG_SYSTEM,
  code: 'pcp-referral-phase-3-complete',
  display: 'PCP Referral Phase 3 Complete',
};

export const PCP_REFERRAL_PENDING_STOPBANG_TAG: Coding = {
  system: TAG_SYSTEM,
  code: 'pcp-referral-pending-stopbang',
  display: 'PCP Referral Pending STOP-BANG',
};

export const PCP_REFERRAL_PENDING_ESS_TAG: Coding = {
  system: TAG_SYSTEM,
  code: 'pcp-referral-pending-ess',
  display: 'PCP Referral Pending ESS',
};

export const PCP_REFERRAL_PENDING_FILLIN_TAG: Coding = {
  system: TAG_SYSTEM,
  code: 'pcp-referral-pending-fillin',
  display: 'PCP Referral Pending Fill In',
};

export const PCP_REFERRAL_MISSING_STOPBANG_TAG: Coding = {
  system: TAG_SYSTEM,
  code: 'pcp-referral-missing-stopbang',
  display: 'PCP Referral Missing STOP-BANG',
};

export const PCP_REFERRAL_MISSING_ESS_TAG: Coding = {
  system: TAG_SYSTEM,
  code: 'pcp-referral-missing-ess',
  display: 'PCP Referral Missing ESS',
};

export const PCP_REFERRAL_FINISHED_TAG: Coding = {
  system: TAG_SYSTEM,
  code: 'pcp-referral-finished',
  display: 'PCP Referral Finished',
};

// --- HSAT triage outcome tags ---

export const TRIAGE_TAGS = {
  hsatEligible: {
    system: TAG_SYSTEM,
    code: 'hsat-eligible',
    display: 'HSAT Eligible',
  },
  hsatNotEligible: {
    system: TAG_SYSTEM,
    code: 'hsat-not-eligible',
    display: 'HSAT Not Eligible',
  },
  hsatReviewRequired: {
    system: TAG_SYSTEM,
    code: 'hsat-review-required',
    display: 'HSAT Review Required',
  },
  insuranceRejected: {
    system: TAG_SYSTEM,
    code: 'insurance-rejected',
    display: 'Insurance Rejected',
  },
} as const;

// --- HSAT patient approval tags ---

export const HSAT_PATIENT_APPROVAL_PENDING_TAG: Coding = {
  system: HSAT_STATUS_TAG_SYSTEM,
  code: 'patient-approval-pending',
  display: 'Patient Approval Pending',
};

export const HSAT_ORDERED_TAG: Coding = {
  system: HSAT_STATUS_TAG_SYSTEM,
  code: 'hsat-ordered',
  display: 'Ordered',
};

export const HSAT_PARTIAL_RESULTS_TAG: Coding = {
  system: HSAT_STATUS_TAG_SYSTEM,
  code: 'hsat-partial-results',
  display: 'Partial Results Available',
};

export const HSAT_FINAL_RESULTS_TAG: Coding = {
  system: HSAT_STATUS_TAG_SYSTEM,
  code: 'hsat-final-results',
  display: 'Final Results Available',
};

export const HSAT_STATUS_LABELS: Record<string, string> = {
  [HSAT_PATIENT_APPROVAL_PENDING_TAG.code as string]: HSAT_PATIENT_APPROVAL_PENDING_TAG.display as string,
  [HSAT_ORDERED_TAG.code as string]: HSAT_ORDERED_TAG.display as string,
  [HSAT_PARTIAL_RESULTS_TAG.code as string]: HSAT_PARTIAL_RESULTS_TAG.display as string,
  [HSAT_FINAL_RESULTS_TAG.code as string]: HSAT_FINAL_RESULTS_TAG.display as string,
};

/**
 * Removes all tags with the same system as `newTag`, then appends `newTag`.
 * Use this to swap one tag for another within the same tagging system.
 */
export function replaceTag(tags: Coding[], newTag: Coding): Coding[] {
  return tags.filter((t) => t.system !== newTag.system).concat(newTag);
}

/** Maps triage tag codes to human-readable recommendation labels for display. */
export const TRIAGE_TAGS_LABELS: Record<string, string> = {
  [TRIAGE_TAGS.hsatEligible.code]: 'HSAT Eligible',
  [TRIAGE_TAGS.hsatNotEligible.code]: 'HSAT Ineligible',
  [TRIAGE_TAGS.hsatReviewRequired.code]: 'Review Required',
  [TRIAGE_TAGS.insuranceRejected.code]: 'Insurance Rejected',
};

// --- Coverage eligibility status tags ---

export const INSURANCE_ELIGIBILITY_ELIGIBLE_TAG: Coding = {
  system: POLARIS_INSURANCE_ELIGIBILITY_STATUS_TAG_SYSTEM,
  code: 'eligible',
  display: 'Eligible',
};

export const INSURANCE_ELIGIBILITY_INELIGIBLE_TAG: Coding = {
  system: POLARIS_INSURANCE_ELIGIBILITY_STATUS_TAG_SYSTEM,
  code: 'ineligible',
  display: 'Ineligible',
};

/** Derives the eligibility tag from a CoverageEligibilityResponse. */
export function getInsuranceEligibilityTag(response: CoverageEligibilityResponse): Coding {
  if (response.error && response.error.length > 0) {
    return INSURANCE_ELIGIBILITY_INELIGIBLE_TAG;
  }
  if (response.insurance?.[0]?.inforce === true) {
    return INSURANCE_ELIGIBILITY_ELIGIBLE_TAG;
  }
  return INSURANCE_ELIGIBILITY_INELIGIBLE_TAG;
}
